import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Typography,
  Form,
  InputNumber,
  Button,
  Alert,
  Progress,
  Space,
  Divider,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Switch
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  ApiOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  getVisitorConfig,
  updateVisitorConfig, // Use Redux action instead of fetch
  clearMessages
} from '../../features/visitor/visitorLimitsSlice';

const { Title, Text } = Typography;

const VisitorAccounts = () => {
  const dispatch = useDispatch();
  const { config, configLoading, error, success } = useSelector(
    (state) => state.visitorLimits
  );
  
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(getVisitorConfig());
  }, [dispatch]);

  useEffect(() => {
    if (config) {
      form.setFieldsValue(config);
    }
  }, [config, form]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
      setHasChanges(false);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  const handleFormChange = () => {
    setHasChanges(true);
  };

  // Use Redux action instead of direct fetch
  const handleSubmit = async (values) => {
    try {
      await dispatch(updateVisitorConfig(values)).unwrap();
      setHasChanges(false);
    } catch (error) {
      // Error handled by useEffect
    }
  };

  const handleReset = () => {
    form.setFieldsValue(config);
    setHasChanges(false);
  };

  const handleRefresh = () => {
    dispatch(getVisitorConfig());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
              <EyeOutlined className="text-xl text-white" />
            </div>
            <div>
              <Title level={3} className="mb-1">Visitor Management</Title>
              <Text className="text-gray-600">Configure visitor request limits and restrictions</Text>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={configLoading}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Current Configuration Overview */}
      <Card 
        title="Current Configuration"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Daily Request Limit"
              value={config.visitorRequestLimit}
              prefix={<ApiOutlined />}
              suffix="requests/day"
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Rate Limit"
              value={config.visitorRateLimit}
              prefix={<ClockCircleOutlined />}
              suffix="requests/min"
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Tracking Status"
              value={config.trackVisitorRequests ? 'Enabled' : 'Disabled'}
              prefix={<EyeOutlined />}
              valueStyle={{ 
                color: config.trackVisitorRequests ? '#52c41a' : '#ff4d4f' 
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Configuration Form */}
      <Card 
        title="Update Visitor Settings"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          initialValues={config}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Daily Request Limit"
                name="visitorRequestLimit"
                rules={[
                  { required: true, message: 'Please set daily request limit' },
                  { type: 'number', min: 1, max: 1000, message: 'Must be between 1 and 1000' }
                ]}
                extra="Maximum write operations per day for visitors"
              >
                <InputNumber
                  min={1}
                  max={1000}
                  style={{ width: '100%' }}
                  suffix="requests/day"
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={8}>
              <Form.Item
                label="Rate Limit"
                name="visitorRateLimit"
                rules={[
                  { required: true, message: 'Please set rate limit' },
                  { type: 'number', min: 1, max: 100, message: 'Must be between 1 and 100' }
                ]}
                extra="Maximum write operations per minute for visitors"
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  suffix="requests/min"
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={8}>
              <Form.Item
                label="Track Visitor Requests"
                name="trackVisitorRequests"
                valuePropName="checked"
                extra="Enable analytics and monitoring for visitor requests"
              >
                <Switch 
                  checkedChildren="Enabled" 
                  unCheckedChildren="Disabled" 
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div className="flex justify-between items-center">
            <div>
              {hasChanges && (
                <Text type="warning" className="text-sm">
                  You have unsaved changes
                </Text>
              )}
            </div>
            <Space>
              <Button 
                onClick={handleReset}
                disabled={!hasChanges}
              >
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={configLoading}
                disabled={!hasChanges}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      {/* Information Card */}
      <Card 
        title="Visitor Limits Information"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <div className="space-y-3 text-sm">
          <div className="flex items-start space-x-2">
            <Tag color="blue">Info</Tag>
            <Text>Limits apply only to POST, PUT, PATCH, DELETE requests (GET requests are unlimited)</Text>
          </div>
          <div className="flex items-start space-x-2">
            <Tag color="orange">Warning</Tag>
            <Text>Visitors exceeding daily limits will receive 429 (Too Many Requests) responses</Text>
          </div>
          <div className="flex items-start space-x-2">
            <Tag color="green">Feature</Tag>
            <Text>Request tracking helps monitor visitor behavior and system usage patterns</Text>
          </div>
          <div className="flex items-start space-x-2">
            <Tag color="purple">Security</Tag>
            <Text>Rate limiting prevents abuse and ensures fair resource usage</Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default VisitorAccounts;